// StoreManagementComponents.js - Sub-components for Store Management
import React, { useEffect } from 'react';

// Products Tab Component
export const ProductsTab = ({
  products,
  categories,
  productSearchTerm,
  setProductSearchTerm,
  filterCategory,
  setFilterCategory,
  filterStatus,
  setFilterStatus,
  handleEditProduct,
  handleDeleteProduct,
  setShowProductModal,
  resetProductForm,
  getStatusBadgeClass,
  formatCurrency,
  API_BASE,
  ARCA_COLORS
}) => {
  // Use BACKEND_URL for static file serving
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  if (!BACKEND_URL) {
    console.error('❌ CRITICAL: REACT_APP_BACKEND_URL not configured in StoreManagementComponents!');
  }
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        {/* Search and Filters */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search products..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="inactive">Inactive</option>
              <option value="discontinued">Discontinued</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
          </div>
          <div className="col-md-2">
            <button
              className="btn w-100"
              style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}
              onClick={() => {
                resetProductForm();
                setShowProductModal(true);
              }}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Add Product
            </button>
          </div>
        </div>

        {/* Products Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th style={{ width: '60px' }}>Image</th>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No products found
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      {product.main_image ? (
                        <img
                          src={product.main_image.startsWith('http') ? product.main_image : `${BACKEND_URL}${product.main_image}`}
                          alt={product.name}
                          className="rounded"
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="bg-light rounded d-flex align-items-center justify-content-center"
                        style={{
                          width: '50px',
                          height: '50px',
                          display: product.main_image ? 'none' : 'flex'
                        }}
                      >
                        <i className="bi bi-image text-muted"></i>
                      </div>
                    </td>
                    <td>
                      <div className="fw-medium">{product.name}</div>
                      <div className="small text-muted">SKU: {product.sku || 'N/A'}</div>
                      {product.brand && (
                        <div className="small text-muted">Brand: {product.brand}</div>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">
                        {product.category_name || 'Uncategorized'}
                      </span>
                    </td>
                    <td>
                      <div className="fw-medium">{formatCurrency(product.price)}</div>
                      {product.member_price && (
                        <div className="small text-success">
                          Member: {formatCurrency(product.member_price)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className={product.stock_quantity < product.low_stock_threshold ? 'text-danger fw-medium' : ''}>
                        {product.stock_quantity}
                      </div>
                      {product.stock_quantity < product.low_stock_threshold && (
                        <small className="text-danger">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Low Stock
                        </small>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(product.status)}`}>
                        {product.status}
                      </span>
                      {product.is_featured === 1 && (
                        <span className="badge bg-warning text-dark ms-1">
                          <i className="bi bi-star-fill"></i>
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="btn-group" role="group">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEditProduct(product)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteProduct(product.id)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Orders Tab Component
export const OrdersTab = ({
  orders,
  orderSearchTerm,
  setOrderSearchTerm,
  handleUpdateOrderStatus,
  getStatusBadgeClass,
  formatCurrency,
  formatDate,
  ARCA_COLORS
}) => {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        {/* Search */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search orders..."
                value={orderSearchTerm}
                onChange={(e) => setOrderSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th style={{ width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    <i className="bi bi-cart-x fs-1 d-block mb-2"></i>
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <div className="fw-medium">{order.order_number}</div>
                    </td>
                    <td>
                      <div>{order.customer_name || 'N/A'}</div>
                      <div className="small text-muted">{order.customer_email}</div>
                    </td>
                    <td className="fw-medium">{formatCurrency(order.total_amount)}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td>{formatDate(order.created_at)}</td>
                    <td>
                      <div className="dropdown">
                        <button
                          className="btn btn-sm dropdown-toggle"
                          style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}
                          type="button"
                          data-bs-toggle="dropdown"
                        >
                          Update Status
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateOrderStatus(order.id, 'pending')}
                            >
                              Pending
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateOrderStatus(order.id, 'confirmed')}
                            >
                              Confirmed
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateOrderStatus(order.id, 'processing')}
                            >
                              Processing
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateOrderStatus(order.id, 'shipped')}
                            >
                              Shipped
                            </button>
                          </li>
                          <li>
                            <button
                              className="dropdown-item"
                              onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
                            >
                              Delivered
                            </button>
                          </li>
                          <li><hr className="dropdown-divider" /></li>
                          <li>
                            <button
                              className="dropdown-item text-danger"
                              onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}
                            >
                              Cancelled
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Product Modal Component - Continues...
export const ProductModal = ({
  show,
  onHide,
  editingProduct,
  productForm,
  setProductForm,
  categories,
  imagePreview,
  handleImageChange,
  uploadingImage,
  handleProductSubmit,
  ARCA_COLORS
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0';
      console.log('✅ Modal opened, preventing body scroll');
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0';
      console.log('❌ Modal closed, restoring body scroll');
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0';
    };
  }, [show]);

  if (!show) return null;

  console.log('🎨 Rendering ProductModal with categories:', categories.length);

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1040
        }}
        onClick={onHide}
      ></div>

      {/* Modal Dialog */}
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'auto',
          zIndex: 1050,
          paddingRight: '0'
        }}
      >
        <div className="modal-dialog modal-lg" style={{
          margin: '1.75rem auto',
          maxWidth: '900px',
          minHeight: '200px'
        }}>
          <div
            className="modal-content"
            style={{
              border: 'none',
              borderRadius: '0.5rem',
              boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.15)',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header" style={{
              backgroundColor: ARCA_COLORS.primary,
              color: 'white',
              borderBottom: 'none',
              padding: '1.5rem',
              flexShrink: 0
            }}>
              <h5 className="modal-title fw-bold" style={{ fontSize: '1.25rem' }}>
                <i className="bi bi-box-seam me-2" style={{ fontSize: '1.5rem' }}></i>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onHide}
                style={{ fontSize: '1rem' }}
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleProductSubmit} encType="multipart/form-data" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{
                padding: '1.5rem',
                overflowY: 'auto',
                flex: 1
              }}>
                <div className="row g-3">
                  {/* Product Image */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Product Image</label>
                    <div className="border rounded p-3 text-center">
                      {imagePreview && (
                        <div className="position-relative d-inline-block mb-2">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="rounded"
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                          />
                        </div>
                      )}
                      {!imagePreview && (
                        <div className="text-muted mb-2">
                          <i className="bi bi-image fs-1 d-block mb-2"></i>
                          No image selected
                        </div>
                      )}
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      <small className="text-muted">Max size: 5MB. Formats: JPG, PNG, GIF, WebP</small>
                    </div>
                  </div>

                  {/* Product Name */}
                  <div className="col-md-8">
                    <label className="form-label fw-medium">Product Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      required
                    />
                  </div>

                  {/* SKU */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">SKU</label>
                    <input
                      type="text"
                      className="form-control"
                      value={productForm.sku}
                      onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                    />
                  </div>

                  {/* Short Description */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Description</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={productForm.short_description}
                      onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })}
                      placeholder="Enter product description"
                    ></textarea>
                  </div>

                  {/* Brand */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Brand</label>
                    <input
                      type="text"
                      className="form-control"
                      value={productForm.brand}
                      onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                    />
                  </div>

                  {/* Manufacturer */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Manufacturer</label>
                    <input
                      type="text"
                      className="form-control"
                      value={productForm.manufacturer}
                      onChange={(e) => setProductForm({ ...productForm, manufacturer: e.target.value })}
                    />
                  </div>

                  {/* Category */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Category</label>
                    <select
                      className="form-select"
                      value={productForm.category_id || ''}
                      onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })}
                    >
                      <option value="">Select Category</option>
                      {categories && categories.length > 0 ? (
                        categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))
                      ) : (
                        <option value="" disabled>No categories available</option>
                      )}
                    </select>
                  </div>

                  {/* Status */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Status</label>
                    <select
                      className="form-select"
                      value={productForm.status}
                      onChange={(e) => setProductForm({ ...productForm, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="discontinued">Discontinued</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>

                  {/* Price */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Price (USD) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={productForm.price}
                      onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                      required
                    />
                  </div>

                  {/* Compare Price */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Compare Price</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={productForm.compare_price}
                      onChange={(e) => setProductForm({ ...productForm, compare_price: e.target.value })}
                    />
                  </div>

                  {/* Member Price */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Member Price</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={productForm.member_price}
                      onChange={(e) => setProductForm({ ...productForm, member_price: e.target.value })}
                    />
                  </div>

                  {/* Stock Quantity */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Stock Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      value={productForm.stock_quantity}
                      onChange={(e) => setProductForm({ ...productForm, stock_quantity: e.target.value })}
                    />
                  </div>

                  {/* Low Stock Threshold */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Low Stock Alert</label>
                    <input
                      type="number"
                      className="form-control"
                      value={productForm.low_stock_threshold}
                      onChange={(e) => setProductForm({ ...productForm, low_stock_threshold: e.target.value })}
                    />
                  </div>

                  {/* Weight */}
                  <div className="col-md-4">
                    <label className="form-label fw-medium">Weight (kg)</label>
                    <input
                      type="number"
                      step="0.001"
                      className="form-control"
                      value={productForm.weight}
                      onChange={(e) => setProductForm({ ...productForm, weight: e.target.value })}
                    />
                  </div>

                  {/* Product Condition */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Condition</label>
                    <select
                      className="form-select"
                      value={productForm.product_condition}
                      onChange={(e) => setProductForm({ ...productForm, product_condition: e.target.value })}
                    >
                      <option value="new">New</option>
                      <option value="refurbished">Refurbished</option>
                      <option value="used">Used</option>
                    </select>
                  </div>

                  {/* Checkboxes */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Product Options</label>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={productForm.is_featured}
                        onChange={(e) => setProductForm({ ...productForm, is_featured: e.target.checked })}
                      />
                      <label className="form-check-label">Featured Product</label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={productForm.is_digital}
                        onChange={(e) => setProductForm({ ...productForm, is_digital: e.target.checked })}
                      />
                      <label className="form-check-label">Digital Product</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{
                borderTop: '1px solid #dee2e6',
                padding: '1.25rem 1.5rem',
                backgroundColor: '#f8f9fa',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onHide}
                  style={{
                    padding: '0.5rem 1.5rem',
                    fontWeight: '500',
                    fontSize: '1rem',
                    minWidth: '100px'
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn"
                  style={{
                    backgroundColor: ARCA_COLORS.primary,
                    color: 'white',
                    padding: '0.5rem 1.5rem',
                    fontWeight: '500',
                    border: 'none',
                    fontSize: '1rem',
                    minWidth: '150px'
                  }}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
